/**
 * useEarningsEstimate — "what will I earn on this order?" via
 * `POST /v1/online-ordering/earnings/estimate`. The backend applies the
 * loyalty program's earn rules (e.g. 100% cashback on a first order), so the
 * number shown at checkout is the number the customer gets.
 */

import { useEffect, useMemo, useState } from "react";

import { loyaltyApi, type EarningsEstimate } from "@/api/services";

export function useEarningsEstimate(input: { orderDraft: unknown | null; enabled: boolean }) {
  const { orderDraft, enabled } = input;
  const [estimate, setEstimate] = useState<EarningsEstimate | null>(null);
  const [loading, setLoading] = useState(false);

  const draftKey = useMemo(() => {
    if (!orderDraft || !enabled) return null;
    try {
      return JSON.stringify(orderDraft);
    } catch {
      return null;
    }
  }, [orderDraft, enabled]);

  useEffect(() => {
    if (!draftKey || !orderDraft) {
      setEstimate(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loyaltyApi
      .estimateEarnings(orderDraft)
      .then((res) => {
        if (!cancelled) setEstimate(res ?? null);
      })
      .catch(() => {
        // Informational only: no loyalty program or a hiccup just hides the card.
        if (!cancelled) setEstimate(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftKey, orderDraft]);

  const earnsSomething =
    !!estimate && ((estimate.estimatedCashback ?? 0) > 0 || (estimate.estimatedPoints ?? 0) > 0);

  return { estimate, loading, earnsSomething };
}
